import type {
  ChartWidgetSettings,
  WidgetPlacement,
  WidgetSettings,
  WidgetsSlice,
  WorkspaceSliceCreator,
  WorkspaceSnapshot,
  WorkspaceWidget,
} from "./workspace.types";
import { normalizeWorkspaceSymbol } from "./workspace.utils";

const hydrateChartSettings = (
  settings: ChartWidgetSettings,
): ChartWidgetSettings => ({
  kind: "chart",
  state: structuredClone(settings.state),
  ...(settings.comparisonSymbols?.length
    ? {
        comparisonSymbols: settings.comparisonSymbols.map(
          normalizeWorkspaceSymbol,
        ),
      }
    : settings.comparisonSymbol
      ? {
          comparisonSymbols: [
            normalizeWorkspaceSymbol(settings.comparisonSymbol),
          ],
        }
      : {}),
  ...(settings.showOpenLimitOrders !== undefined
    ? { showOpenLimitOrders: settings.showOpenLimitOrders }
    : {}),
  ...(settings.showHistoricalOrderFills !== undefined
    ? { showHistoricalOrderFills: settings.showHistoricalOrderFills }
    : {}),
});

const mapsFromSnapshot = (snapshot: WorkspaceSnapshot) => {
  const widgets: Record<string, WorkspaceWidget> = {};
  const placements: Record<string, WidgetPlacement> = {};
  const widgetSettings: Record<string, WidgetSettings> = {};

  for (const w of snapshot.widgets) {
    widgets[w.id] = {
      id: w.id,
      kind: w.kind,
      groupId: w.groupId ?? null,
      ...(w.localSymbol
        ? { localSymbol: normalizeWorkspaceSymbol(w.localSymbol) }
        : {}),
      ...(w.orderTicketPrefs
        ? { orderTicketPrefs: { ...w.orderTicketPrefs } }
        : {}),
      ...(w.kind === "watchlist" && w.watchlistId
        ? { watchlistId: w.watchlistId }
        : {}),
    };
  }

  for (const p of snapshot.placements) {
    placements[p.widgetId] = { ...p };
  }

  for (const [widgetId, settings] of Object.entries(snapshot.widgetSettings)) {
    widgetSettings[widgetId] =
      settings.kind === "chart"
        ? hydrateChartSettings(settings)
        : {
            kind: "portfolioHistory",
            comparisons: settings.comparisons.map((comparison) => ({
              ...comparison,
            })),
          };
  }

  return { widgets, placements, widgetSettings };
};

export const createWidgetsSlice: WorkspaceSliceCreator<WidgetsSlice> = (
  set,
  get,
) => ({
  widgets: {},
  placements: {},

  addWidget: ({ id, kind, placement, localSymbol }) => {
    set((draft) => {
      draft.widgets[id] = {
        id,
        kind,
        groupId: null,
        ...(["chart", "watchlist"].includes(kind) && localSymbol !== undefined
          ? { localSymbol: normalizeWorkspaceSymbol(localSymbol) }
          : {}),
      };
      draft.placements[id] = { ...placement };
      if (kind === "orderTicket") {
        const order = draft.nextTicketCreationOrder;
        draft.tickets[id] = {
          localSymbol:
            localSymbol !== undefined
              ? normalizeWorkspaceSymbol(localSymbol)
              : "",
          legs: [],
          lastInteractedAt: 0,
          createdOrder: order,
        };
        draft.nextTicketCreationOrder += 1;
      }
    });
  },

  removeWidget: (widgetId) => {
    set((draft) => {
      const widget = draft.widgets[widgetId];
      if (!widget) {
        return;
      }
      delete draft.widgets[widgetId];
      delete draft.placements[widgetId];
      delete draft.widgetSettings[widgetId];
      if (widget.kind === "orderTicket") {
        delete draft.tickets[widgetId];
      }
      // The group is NOT removed — empty groups persist until explicitly deleted.
    });
  },

  setWidgetLocalSymbol: (widgetId, symbol) => {
    set((draft) => {
      const widget = draft.widgets[widgetId];
      // No-op for grouped widgets; the group is the source of truth.
      if (!widget || widget.groupId !== null) {
        return;
      }

      const normalized = symbol ? normalizeWorkspaceSymbol(symbol) : undefined;
      if (widget.kind === "chart" || widget.kind === "watchlist") {
        if (normalized) {
          widget.localSymbol = normalized;
        } else {
          delete widget.localSymbol;
        }
      } else if (widget.kind === "orderTicket") {
        const ticket = draft.tickets[widgetId];
        if (ticket) {
          ticket.localSymbol = normalized ?? "";
        }
      }
    });
  },

  setOrderTicketPrefs: (widgetId, prefs) => {
    set((draft) => {
      const widget = draft.widgets[widgetId];
      if (widget?.kind !== "orderTicket") {
        return;
      }
      widget.orderTicketPrefs = { ...prefs };
    });
  },

  setWidgetWatchlistId: (widgetId, watchlistId) => {
    set((draft) => {
      const widget = draft.widgets[widgetId];
      if (widget?.kind !== "watchlist") {
        return;
      }
      if (watchlistId) {
        widget.watchlistId = watchlistId;
      } else {
        delete widget.watchlistId;
      }
    });
  },

  setPlacements: (placements) => {
    set((draft) => {
      draft.placements = {};
      for (const p of placements) {
        draft.placements[p.widgetId] = { ...p };
      }
    });
  },

  resetWorkspace: () => {
    set((draft) => {
      draft.widgets = {};
      draft.placements = {};
      draft.widgetSettings = {};
      draft.tickets = {};
      draft.nextTicketCreationOrder = 0;
    });
  },

  applySnapshot: (snapshot) => {
    set((draft) => {
      const { widgets, placements, widgetSettings } =
        mapsFromSnapshot(snapshot);
      const retainedTickets = draft.tickets;
      draft.widgets = widgets;
      draft.placements = placements;
      draft.widgetSettings = widgetSettings;
      draft.tickets = {};

      for (const w of snapshot.widgets) {
        if (w.kind !== "orderTicket") {
          continue;
        }
        const retained = retainedTickets[w.id];
        if (retained) {
          draft.tickets[w.id] = retained;
        }
      }

      draft.nextTicketCreationOrder =
        Math.max(
          -1,
          ...Object.values(draft.tickets).map((ticket) => ticket.createdOrder),
        ) + 1;

      // New structural tickets start blank; retained tickets keep live form state.
      for (const w of snapshot.widgets) {
        if (w.kind !== "orderTicket" || draft.tickets[w.id]) {
          continue;
        }
        draft.tickets[w.id] = {
          localSymbol: "",
          legs: [],
          lastInteractedAt: 0,
          createdOrder: draft.nextTicketCreationOrder,
        };
        draft.nextTicketCreationOrder += 1;
      }
    });
  },

  toSnapshot: () => {
    const state = get();

    const widgets = (
      Object.values(state.widgets) as Array<WorkspaceWidget | undefined>
    )
      .filter((w): w is WorkspaceWidget => w !== undefined)
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((w) => {
        const entry: WorkspaceSnapshot["widgets"][number] = {
          id: w.id,
          kind: w.kind,
        };
        if (w.groupId !== null) {
          entry.groupId = w.groupId;
        }
        if ((w.kind === "chart" || w.kind === "watchlist") && w.localSymbol) {
          entry.localSymbol = w.localSymbol;
        }
        if (w.kind === "orderTicket" && w.orderTicketPrefs) {
          entry.orderTicketPrefs = { ...w.orderTicketPrefs };
        }
        if (w.kind === "watchlist" && w.watchlistId) {
          entry.watchlistId = w.watchlistId;
        }
        return entry;
      });

    const placements = (
      Object.values(state.placements) as Array<WidgetPlacement | undefined>
    )
      .filter((p): p is WidgetPlacement => p !== undefined)
      .sort((a, b) => a.widgetId.localeCompare(b.widgetId))
      .map((p) => ({ ...p }));

    const snapshot: WorkspaceSnapshot = {
      engine: "rgl",
      placements,
      widgets,
      widgetSettings: Object.fromEntries(
        Object.entries(state.widgetSettings)
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([widgetId, settings]) => [
            widgetId,
            settings.kind === "chart"
              ? {
                  kind: "chart" as const,
                  state: structuredClone(settings.state),
                  ...(settings.comparisonSymbols?.length
                    ? { comparisonSymbols: [...settings.comparisonSymbols] }
                    : {}),
                  ...(settings.showOpenLimitOrders !== undefined
                    ? { showOpenLimitOrders: settings.showOpenLimitOrders }
                    : {}),
                  ...(settings.showHistoricalOrderFills !== undefined
                    ? {
                        showHistoricalOrderFills:
                          settings.showHistoricalOrderFills,
                      }
                    : {}),
                }
              : {
                  kind: "portfolioHistory" as const,
                  comparisons: settings.comparisons.map((comparison) => ({
                    ...comparison,
                  })),
                },
          ]),
      ),
    };
    return snapshot;
  },
});
